import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  List,
  ListItem,
  ListIcon,
  Link,
  useColorModeValue,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { FaShieldAlt } from 'react-icons/fa';
import { PrivacyPolicyPresentationProps } from '../types';
import { SectionAnchor } from '../../../shared/components';

const PrivacyPolicyPresentation: React.FC<PrivacyPolicyPresentationProps> = ({
  id,
  heading,
  lastUpdated,
  sections,
  disclaimer
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const sectionBgColor = useColorModeValue('gray.50', 'gray.700');
  const disclaimerBgColor = useColorModeValue('blue.50', 'blue.900');
  const disclaimerBorderColor = useColorModeValue('blue.100', 'blue.700');

  return (
    <>
      <SectionAnchor id={id} />
      <Box 
        as="section"
        pt={{ base: 24, md: 32 }} 
        pb={{ base: 16, md: 24 }}
        bg={bgColor}
      >
        <Container maxW="container.lg">
          <VStack spacing={8} align="start">
            <Box width="full" textAlign="center" mb={8}>
              <Flex justify="center" mb={6}>
                <Icon 
                  as={FaShieldAlt} 
                  boxSize={16} 
                  color="brand.primary.base" 
                  aria-hidden="true"
                />
              </Flex>
              <Heading 
                as="h1" 
                size="2xl" 
                mb={4}
                color={headingColor}
              >
                {heading}
              </Heading>
              <Text fontSize="md" color={textColor}>
                Last Updated: {lastUpdated}
              </Text>
            </Box>

            {disclaimer && (
              <Box 
                width="full" 
                p={6} 
                borderRadius="lg" 
                bg={disclaimerBgColor}
                borderWidth="1px"
                borderColor={disclaimerBorderColor}
                mb={8}
              >
                <Text fontSize="md" fontWeight="medium" color={textColor}>
                  {disclaimer}
                </Text>
              </Box>
            )}

            {sections.map((section, sectionIndex) => (
              <Box 
                key={`section-${sectionIndex}`}
                width="full" 
                p={8} 
                borderRadius="lg" 
                bg={sectionBgColor}
                boxShadow="sm"
              >
                <Heading as="h2" size="lg" mb={4} color={headingColor}>
                  {section.title}
                </Heading>
                
                <VStack spacing={4} align="start">
                  {section.paragraphs.map((paragraph, paragraphIndex) => (
                    <Text 
                      key={`paragraph-${sectionIndex}-${paragraphIndex}`} 
                      color={textColor}
                    >
                      {paragraph}
                    </Text>
                  ))}
                  
                  {section.bulletPoints && section.bulletPoints.length > 0 && (
                    <List spacing={2} mt={2} width="full">
                      {section.bulletPoints.map((point, pointIndex) => (
                        <ListItem 
                          key={`point-${sectionIndex}-${pointIndex}`}
                          display="flex"
                        >
                          <ListIcon as={CheckCircleIcon} color="green.500" mt={1} />
                          <Text color={textColor}>{point}</Text>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </VStack>
              </Box>
            ))}

            <Divider my={6} />
            
            <Box width="full" textAlign="center" mt={6}>
              <Text fontSize="sm" color={textColor}>
                If you have any questions about this Privacy Policy, please contact us at{' '}
                <Link 
                  href="mailto:privacy@az1.ai" 
                  color="brand.primary.base"
                  _hover={{ color: 'brand.primary.dark', textDecoration: 'underline' }}
                >
                  privacy@az1.ai
                </Link>
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicyPresentation;